import { Component, computed, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatChipsModule } from '@angular/material/chips';
import { Store } from '@ngrx/store';
import { HotToastService } from '@ngxpert/hot-toast';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { combineLatest, forkJoin, Observable, of, switchMap, tap } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { MaterialModule } from '../../../../../shared/material.module';
import { TabToolbarComponent } from '../tab-toolbar/tab-toolbar.component';
import { HasUnsavedChanges } from '../../../../../guards/unsaved-changes.guard';
import { ConfirmationDialogService } from '../../../../../services/confirmation-dialog.service';
import { Helpers } from '../../../../../utilities/helpers';
import { CompanyService } from '../../../../../services/companies/company.service';
import { CompanyDetailsService } from '../../../../../services/companies/company-details.service';
import { CompanyDetailsDto } from '../../../../../models/api/companyDetailsDto';
import { CreateCompanyRequest } from '../../../../../models/api/createCompanyRequest';
import { CompanyInactivePeriodService } from '../../../../../services/companies/company-inactive-period.service';
import { CleanupSummaryDto, CompanyInactivePeriodDto } from '../../../../../models/api/companyInactivePeriodDto';
import { CreateCompanyInactivePeriodRequest } from '../../../../../models/api/companyInactivePeriodDto';
import { LookupDataService } from '../../../../../services/lookupDataService.service';
import { CountryFlat } from '../../../../../models/country';
import { IndustryFlat } from '../../../../../models/industry';
import { CompanyDeregistrationReason } from '../../../../../models/companyDeregistrationReason';
import { LutService } from '../../../../../services/lut.service';
import { selectTabYear, selectDefaultYear, selectInactivePeriods, selectInactivePeriodsLoaded } from '../../../../../store/selectors/company-form.selectors';
import { setTabYear, loadInactivePeriods } from '../../../../../store/actions/company-form.actions';
import { ApiErrorResponse } from '../../../../../models/api/apiErrorResponse';
import { ErrorCodes } from '../../../../../models/api/errorCodes';
import { CompanyOtherCountriesService } from '../../../../../services/companies/company-other-countries.service';
import { CompanyOtherCountriesDto } from '../../../../../models/api/companyOtherCountriesDto';
import { CompanyStockExchangesService } from '../../../../../services/companies/company-stock-exchanges.service';
import { CompanyStockExchangesDto } from '../../../../../models/api/companyStockExchangesDto';
import { StockExchange } from '../../../../../models/stockExchange';
import { CompanyCapitalizationService } from '../../../../../services/companies/company-capitalization.service';
import { CompanyCapitalizationDto } from '../../../../../models/api/companyCapitalizationDto';
import { CompanyStockIndexesService } from '../../../../../services/companies/company-stock-indexes.service';
import { CompanyStockIndexesDto } from '../../../../../models/api/companyStockIndexesDto';
import { Index } from '../../../../../models/index';
import { Currency } from '../../../../../models/currency';
import { CurrentUserState } from '../../../../../state/current-user.state';
import { ReadOnlyTabDirective } from '../../../../../directives/read-only-tab.directive';
import { AppUserCompanyAccessType } from '../../../../../models/enums/appUserCompanyAccessType.enum';

@Component({
  selector: 'app-details-tab',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MaterialModule,
    MatChipsModule,
    NgxSpinnerModule,
    TabToolbarComponent,
    ReadOnlyTabDirective
  ],
  templateUrl: './details-tab.component.html',
  styleUrl: './details-tab.component.scss'
})
export class DetailsTabComponent implements OnInit, HasUnsavedChanges {
  private fb = inject(FormBuilder);
  private route = inject(ActivatedRoute);
  private store = inject(Store);
  private companyDetailsService = inject(CompanyDetailsService);
  private companyInactivePeriodService = inject(CompanyInactivePeriodService);
  private confirmationDialog = inject(ConfirmationDialogService);
  private router = inject(Router);
  private toast = inject(HotToastService);
  private companyService = inject(CompanyService);
  private lookupService = inject(LookupDataService);
  private lutService = inject(LutService);
  private companyOtherCountriesService = inject(CompanyOtherCountriesService);
  private companyStockExchangesService = inject(CompanyStockExchangesService);
  private companyCapitalizationService = inject(CompanyCapitalizationService);
  private companyStockIndexesService = inject(CompanyStockIndexesService);
  private spinnerService = inject(NgxSpinnerService);
  private currentUserState = inject(CurrentUserState);

  /** Tab held read-only: the form is disabled by ReadOnlyTabDirective and row actions are hidden. */
  readonly isReadOnly = computed(() =>
    this.currentUserState.isCompanyTabReadOnly(AppUserCompanyAccessType.Details));

  selectedYear: string = this.currentUserState.defaultMeasurementYear();
  companyId: string | null = null;
  showYearWarning = false;
  currencyAutoFilled = false;

  measurements: string[] = this.currentUserState.accessibleMeasurements();
  inactiveYears: Set<string> = new Set();
  selectedMeasurements: string[] = [];
  private initialMeasurements: string[] = [];
  private existingInactivePeriods: CompanyInactivePeriodDto[] = [];
  // Aggregated cleanup summary from the most recent inactive-period save, so onSave()
  // can surface a toast after the entire forkJoin completes. Reset at the start of each save.
  private lastCleanupSummary: CleanupSummaryDto | null = null;
  measurementsChanged = false;

  selectedOtherCountries: string[] = [];
  private initialOtherCountries: string[] = [];
  otherCountriesChanged = false;

  stockExchanges: StockExchange[] = [];
  selectedStockExchanges: string[] = [];
  private initialStockExchanges: string[] = [];
  stockExchangesChanged = false;

  capitalization: number | null = null;
  private initialCapitalization: number | null = null;
  capitalizationChanged = false;

  capitalizationOptions = [
    { value: 0, label: 'Unknown' },
    { value: 1, label: 'Small' },
    { value: 2, label: 'Medium' },
    { value: 3, label: 'Large' }
  ];

  stockIndices: Index[] = [];
  selectedStockIndexes: string[] = [];
  private initialStockIndexes: string[] = [];
  stockIndexesChanged = false;

  form: FormGroup = this.fb.group({
    companyName: ['', Validators.required],
    address: [''],
    website: [''],
    countryId: [''],
    industryId: [''],
    superSectorId: [''],
    sectorId: [''],
    subSectorId: [''],
    boardType: [null],
    stockSymbol: [''],
    datastreamSymbol: [''],
    isin: [''],
    founder1: [''],
    founder2: [''],
    founder3: [''],
    founder4: [''],
    founder5: [''],
    foundersName: [false],
    yearWentPublic: [''],
    yearOfFounding: [''],
    deletedDate: [''],
    formerName: [''],
    formerNameDescription: [''],
    deregistrationReasonId: [''],
    deregistrationDescription: [''],
    currencyId: ['']
  });

  boardTypeOptions = [
    { value: 0, label: 'Unitary' },
    { value: 1, label: 'Dual' }
  ];

  countries: CountryFlat[] = [];
  allIndustries: IndustryFlat[] = [];
  currencies: Currency[] = [];
  deregistrationReasons: CompanyDeregistrationReason[] = [];

  get industries(): IndustryFlat[] {
    return this.allIndustries.filter(i => !i.parentId);
  }

  get superSectors(): IndustryFlat[] {
    const industryId = this.form.get('industryId')?.value;
    if (!industryId) return [];
    return this.allIndustries.filter(i => i.parentId === industryId);
  }

  get sectors(): IndustryFlat[] {
    const superSectorId = this.form.get('superSectorId')?.value;
    if (!superSectorId) return [];
    return this.allIndustries.filter(i => i.parentId === superSectorId);
  }

  get subSectors(): IndustryFlat[] {
    const sectorId = this.form.get('sectorId')?.value;
    if (!sectorId) return [];
    return this.allIndustries.filter(i => i.parentId === sectorId);
  }

  ngOnInit(): void {
    this.companyId = this.route.snapshot.paramMap.get('id') ?? null;

    const storedYear = this.store.selectSignal(selectTabYear('details'))();
    const defaultYear = this.store.selectSignal(selectDefaultYear)();
    if (storedYear) {
      this.selectedYear = storedYear;
    } else if (defaultYear) {
      this.selectedYear = defaultYear;
      this.store.dispatch(setTabYear({ tabId: 'details', year: defaultYear }));
    } else {
      this.store.dispatch(setTabYear({ tabId: 'details', year: this.selectedYear }));
    }

    this.lookupService.getVisibleCountries<CountryFlat>().subscribe(data => {
      if (data) this.countries = data;
    });

    this.lookupService.getTableStream<IndustryFlat>('industries').subscribe(data => {
      if (data) this.allIndustries = data;
    });

    this.lookupService.getTableStream<StockExchange>('stockExchanges').subscribe(data => {
      if (data) this.stockExchanges = data;
    });

    this.lookupService.getTableStream<Index>('stockIndices').subscribe(data => {
      if (data) this.stockIndices = data;
    });

    this.lookupService.getTableStream<Currency>('currencies').subscribe(data => {
      if (data) this.currencies = data;
    });

    this.lutService.getCompanyDeregistrationReasons().subscribe(data => {
      this.deregistrationReasons = data;
    });

    this.form.get('countryId')?.valueChanges.subscribe(countryId => {
      const country = this.countries.find(c => c.id === countryId);
      if (country?.currencyId) {
        const currencyControl = this.form.get('currencyId');
        if (currencyControl?.value !== country.currencyId) {
          currencyControl?.setValue(country.currencyId);
          this.currencyAutoFilled = true;
          setTimeout(() => this.currencyAutoFilled = false, 2000);
        }
      }
    });

    // Clear child selections when parent changes
    this.form.get('industryId')?.valueChanges.subscribe(() => {
      this.form.get('superSectorId')?.setValue('', { emitEvent: false });
      this.form.get('sectorId')?.setValue('', { emitEvent: false });
      this.form.get('subSectorId')?.setValue('', { emitEvent: false });
    });

    this.form.get('superSectorId')?.valueChanges.subscribe(() => {
      this.form.get('sectorId')?.setValue('', { emitEvent: false });
      this.form.get('subSectorId')?.setValue('', { emitEvent: false });
    });

    this.form.get('sectorId')?.valueChanges.subscribe(() => {
      this.form.get('subSectorId')?.setValue('', { emitEvent: false });
    });

    if (this.companyId) {
      this.loadDetails();
      this.loadInactivePeriods();
      this.loadOtherCountries();
      this.loadStockExchanges();
      this.loadCapitalization();
      this.loadStockIndexes();
    } else {
      // Create mode: select all measurements by default
      this.selectedMeasurements = [...this.measurements];
      this.initialMeasurements = [...this.measurements];
    }
  }

  onYearChange(year: string): void {
    if (this.hasChanges) {
      this.confirmationDialog.openConfirmationDialog(
        'Unsaved Changes',
        'You have unsaved changes. Are you sure you want to switch measurement?',
        'Switch',
        'Stay',
        'swap_horiz',
        'edit'
      ).subscribe(confirmed => {
        if (confirmed) {
          this.applyYearChange(year);
        } else {
          // Reset toolbar dropdown to current year
          const current = this.selectedYear;
          this.selectedYear = '';
          setTimeout(() => this.selectedYear = current);
        }
      });
    } else {
      this.applyYearChange(year);
    }
  }

  private applyYearChange(year: string): void {
    this.selectedYear = year;
    this.store.dispatch(setTabYear({ tabId: 'details', year }));
    this.measurementsChanged = false;
    this.otherCountriesChanged = false;
    this.stockExchangesChanged = false;
    this.capitalizationChanged = false;
    this.stockIndexesChanged = false;
    if (this.companyId) {
      this.loadDetails();
      this.loadOtherCountries();
      this.loadStockExchanges();
      this.loadCapitalization();
      this.loadStockIndexes();
    }
  }

  private loadDetails(): void {
    if (!this.companyId) return;

    this.spinnerService.show();
    this.companyDetailsService.get(this.companyId, parseInt(this.selectedYear)).subscribe({
      next: resource => {
        // Details uses 'present' (record explicitly entered for the year),
        // 'inherited' (older record still in force; effectiveYear is the source year),
        // or 'absent' (no record at all). Inherited is the common case for stable data.
        if (resource.state === 'absent' || resource.data == null) {
          this.form.reset();
          this.form.markAsPristine();
          this.showYearWarning = true;
        } else {
          this.showYearWarning = false;
          this.patchForm(resource.data);
        }
        this.spinnerService.hide();
      },
      error: (error: any) => {
        this.spinnerService.hide();
        const apiError = error.error as ApiErrorResponse;

        if (apiError?.errorCode === ErrorCodes.NFO_ENTITY_NOT_FOUND) {
          // Entity not found - silently handle
        } else {
          this.toast.error('Error loading company details', {
            iconTheme: {
              primary: 'var(--mat-sys-error)',
              secondary: '#FFF',
            }
          });
        }

        console.error('Error loading company details:', error);
      }
    });
  }

  private patchForm(details: CompanyDetailsDto): void {
    // Company name/country are now updated by CompanyFormEffects on year change

    // Patch without emitting events to avoid cascading clears
    this.form.patchValue({
      companyName: details.companyName ?? '',
      address: details.address ?? '',
      website: details.website ?? '',
      countryId: details.countryId ?? '',
      industryId: details.industryId ?? '',
      superSectorId: details.superSectorId ?? '',
      sectorId: details.sectorId ?? '',
      subSectorId: details.subSectorId ?? '',
      boardType: details.boardType ?? null,
      stockSymbol: details.stockSymbol ?? '',
      datastreamSymbol: details.datastreamSymbol ?? '',
      isin: details.isin ?? '',
      founder1: details.founder1 ?? '',
      founder2: details.founder2 ?? '',
      founder3: details.founder3 ?? '',
      founder4: details.founder4 ?? '',
      founder5: details.founder5 ?? '',
      foundersName: details.foundersName ?? false,
      yearWentPublic: Helpers.toDisplayDate(details.yearWentPublic),
      yearOfFounding: Helpers.toDisplayDate(details.yearOfFounding),
      deletedDate: Helpers.toDisplayDate(details.deletedDate),
      formerName: details.formerName ?? '',
      formerNameDescription: details.formerNameDescription ?? '',
      deregistrationReasonId: details.deregistrationReasonId ?? '',
      deregistrationDescription: details.deregistrationDescription ?? '',
      currencyId: details.currencyId ?? ''
    }, { emitEvent: false });

    this.form.markAsPristine();
  }

  onSave(): void {
    const request = this.getCreateRequest();
    if (!request) return;

    if (this.companyId) {
      // Edit mode: upsert details + update inactive periods.
      // Spinner stays up across upsert + the dependent forkJoin (inactive-period save
      // can trigger backend cleanup across ~23 tables and noticeably blocks the UI).
      this.spinnerService.show();
      // Founders are written to every year of the company, so send them only when the user edited
      // them here. Otherwise a save for some other field would push back what this tab loaded
      // (the Family tab may have changed them since), or the blanks of a year with no record yet.
      const founderFieldsIncluded = ['founder1', 'founder2', 'founder3', 'founder4', 'founder5']
        .some(field => this.form.get(field)?.dirty);
      this.companyDetailsService.upsert(this.companyId!, { ...request, founderFieldsIncluded }).subscribe({
        next: () => {
          this.showYearWarning = false;
          const pendingSaves: Observable<any>[] = [];

          if (this.measurementsChanged) {
            pendingSaves.push(this.saveInactivePeriodsAsObservable(this.companyId!));
          }
          if (this.otherCountriesChanged) {
            pendingSaves.push(this.saveOtherCountriesAsObservable(this.companyId!));
          }
          if (this.stockExchangesChanged) {
            pendingSaves.push(this.saveStockExchangesAsObservable(this.companyId!));
          }
          if (this.capitalizationChanged) {
            pendingSaves.push(this.saveCapitalizationAsObservable(this.companyId!));
          }
          if (this.stockIndexesChanged) {
            pendingSaves.push(this.saveStockIndexesAsObservable(this.companyId!));
          }

          if (pendingSaves.length > 0) {
            const measurementsWereChanged = this.measurementsChanged;
            this.lastCleanupSummary = null;
            forkJoin(pendingSaves).subscribe({
              next: () => {
                this.form.markAsPristine();
                this.toast.success('Company details saved successfully');
                this.surfaceCleanupToast();
                if (measurementsWereChanged && this.companyId) {
                  // Refresh the shared inactive-period store so the other tabs'
                  // inactivity banner/save-disable reflect the new periods.
                  this.store.dispatch(loadInactivePeriods({ companyId: this.companyId }));
                }
                this.spinnerService.hide();
              },
              error: () => {
                this.toast.error('Details saved but some related data failed to save');
                this.spinnerService.hide();
              }
            });
          } else {
            this.toast.success('Company details saved successfully');
            this.form.markAsPristine();
            this.spinnerService.hide();
          }
        },
        error: () => {
          this.toast.error('Failed to save company details');
          this.spinnerService.hide();
        }
      });
    } else {
      // Create mode: confirm, then create company + inactive periods
      this.confirmationDialog.openCreateConfirmationDialog(
        'Are you sure you want to create this company?'
      ).subscribe(confirmed => {
        if (!confirmed) return;

        // Spinner only after confirmation, so the confirmation dialog isn't blocked.
        this.spinnerService.show();
        this.companyService.create(request).subscribe({
          next: (company) => {
            const inactivePeriods = this.buildInactivePeriods();
            if (inactivePeriods.length > 0) {
              const requests = inactivePeriods.map(p =>
                this.companyInactivePeriodService.create(company.companyId, p)
              );
              forkJoin(requests).subscribe({
                next: (results) => {
                  this.toast.success('Company created successfully');
                  this.lastCleanupSummary = this.aggregateCleanup(results.map(r => r.cleanup));
                  this.surfaceCleanupToast();
                  this.form.markAsPristine();
                  this.measurementsChanged = false;
                  this.spinnerService.hide();
                  this.router.navigate(['/companies', 'edit', company.companyId]);
                },
                error: () => {
                  this.toast.error('Company created but failed to save inactive periods');
                  this.spinnerService.hide();
                  this.router.navigate(['/companies', 'edit', company.companyId]);
                }
              });
            } else {
              this.toast.success('Company created successfully');
              this.form.markAsPristine();
              this.measurementsChanged = false;
              this.spinnerService.hide();
              this.router.navigate(['/companies', 'edit', company.companyId]);
            }
          },
          error: () => {
            this.toast.error('Failed to create company');
            this.spinnerService.hide();
          }
        });
      });
    }
  }

  private saveInactivePeriodsAsObservable(companyId: string): Observable<any> {
    const deleteRequests = this.existingInactivePeriods.map(p =>
      this.companyInactivePeriodService.delete(companyId, p.id)
    );

    const deleteAll$ = deleteRequests.length > 0 ? forkJoin(deleteRequests) : of([]);

    return deleteAll$.pipe(
      switchMap(() => {
        const newPeriods = this.buildInactivePeriods();
        if (newPeriods.length > 0) {
          const createRequests = newPeriods.map(p =>
            this.companyInactivePeriodService.create(companyId, p)
          );
          return forkJoin(createRequests).pipe(
            tap((results) => {
              this.existingInactivePeriods = results.map(r => r.period);
              this.lastCleanupSummary = this.aggregateCleanup(results.map(r => r.cleanup));
              this.initialMeasurements = [...this.selectedMeasurements];
              this.measurementsChanged = false;
            })
          );
        } else {
          this.existingInactivePeriods = [];
          this.initialMeasurements = [...this.selectedMeasurements];
          this.measurementsChanged = false;
          return of([]);
        }
      })
    );
  }

  private buildInactivePeriods(): CreateCompanyInactivePeriodRequest[] {
    const inactiveYears = this.measurements
      .filter(m => !this.selectedMeasurements.includes(m))
      .map(m => parseInt(m))
      .sort((a, b) => a - b);

    if (inactiveYears.length === 0) return [];

    const maxMeasurement = Math.max(...this.measurements.map(m => parseInt(m)));

    // Group consecutive years into periods
    const periods: CreateCompanyInactivePeriodRequest[] = [];
    let start = inactiveYears[0];
    let prev = inactiveYears[0];

    for (let i = 1; i < inactiveYears.length; i++) {
      if (inactiveYears[i] === prev + 1) {
        prev = inactiveYears[i];
      } else {
        periods.push({
          startDate: `${start}-01-01T00:00:00Z`,
          endDate: `${prev + 1}-01-01T00:00:00Z`
        });
        start = inactiveYears[i];
        prev = inactiveYears[i];
      }
    }
    periods.push({
      startDate: `${start}-01-01T00:00:00Z`,
      endDate: prev >= maxMeasurement ? null : `${prev + 1}-01-01T00:00:00Z`
    });

    return periods;
  }

  getCreateRequest(): CreateCompanyRequest | null {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return null;
    }

    const v = this.form.getRawValue();
    return {
      year: parseInt(this.selectedYear),
      companyName: v.companyName,
      address: v.address || undefined,
      website: v.website || undefined,
      countryId: v.countryId || undefined,
      industryId: v.industryId || undefined,
      superSectorId: v.superSectorId || undefined,
      sectorId: v.sectorId || undefined,
      subSectorId: v.subSectorId || undefined,
      boardType: v.boardType ?? undefined,
      stockSymbol: v.stockSymbol || undefined,
      datastreamSymbol: v.datastreamSymbol || undefined,
      isin: v.isin || undefined,
      founder1: v.founder1 || undefined,
      founder2: v.founder2 || undefined,
      founder3: v.founder3 || undefined,
      founder4: v.founder4 || undefined,
      founder5: v.founder5 || undefined,
      foundersName: v.foundersName ?? false,
      yearWentPublic: Helpers.toIsoDate(v.yearWentPublic) || undefined,
      yearOfFounding: Helpers.toIsoDate(v.yearOfFounding) || undefined,
      deletedDate: Helpers.toIsoDate(v.deletedDate) || undefined,
      formerName: v.formerName || undefined,
      formerNameDescription: v.formerNameDescription || undefined,
      deregistrationReasonId: v.deregistrationReasonId || undefined,
      deregistrationDescription: v.deregistrationDescription || undefined,
      currencyId: v.currencyId || undefined,
    };
  }

  get hasChanges(): boolean {
    return this.form.dirty || this.measurementsChanged || this.otherCountriesChanged || this.stockExchangesChanged || this.capitalizationChanged || this.stockIndexesChanged;
  }

  hasUnsavedChanges(): boolean {
    return this.hasChanges;
  }

  compareById(option1: any, option2: any): boolean {
    return option1 && option2 ? option1 === option2 : option1 === option2;
  }

  clearDeregistrationReason(): void {
    this.form.get('deregistrationReasonId')?.setValue('');
    this.form.get('deregistrationReasonId')?.markAsDirty();
  }

  private loadInactivePeriods(): void {
    if (!this.companyId) return;

    // Inactive periods are company-wide (not year-specific) and CompanyFormEffects
    // already fetches them once into the shared store on company activation. Consume
    // that store value instead of issuing a second identical request. Wait for the
    // 'loaded' flag so we read the freshly-fetched periods, not a stale/empty default.
    combineLatest([
      this.store.select(selectInactivePeriods),
      this.store.select(selectInactivePeriodsLoaded)
    ]).pipe(
      filter(([, loaded]) => loaded),
      take(1)
    ).subscribe(([periods]) => {
      this.existingInactivePeriods = periods;
      this.inactiveYears = new Set<string>();
      for (const period of periods) {
        const startYear = new Date(period.startDate).getFullYear();
        const endYear = period.endDate
          ? new Date(period.endDate).getFullYear()
          : Number(Helpers.defaultMeasurementYear()) + 1;
        for (let y = startYear; y < endYear; y++) {
          this.inactiveYears.add(String(y));
        }
      }
      this.selectedMeasurements = this.measurements.filter(m => !this.inactiveYears.has(m));
      this.initialMeasurements = [...this.selectedMeasurements];
      this.measurementsChanged = false;
    });
  }

  private checkMeasurementsChanged(): void {
    const current = [...this.selectedMeasurements].sort();
    const initial = [...this.initialMeasurements].sort();
    this.measurementsChanged = current.length !== initial.length ||
      current.some((v, i) => v !== initial[i]);
  }

  onMeasurementsSelectionChange(event: any): void {
    this.selectedMeasurements = event.value;
    this.checkMeasurementsChanged();
  }

  selectAllMeasurements(): void {
    this.selectedMeasurements = [...this.measurements];
    this.checkMeasurementsChanged();
  }

  deselectAllMeasurements(): void {
    this.selectedMeasurements = [];
    this.checkMeasurementsChanged();
  }

  // Other Countries

  getCountryName(countryId: string): string {
    return this.countries.find(c => c.id === countryId)?.name || '';
  }

  onOtherCountriesSelectionChange(event: any): void {
    this.selectedOtherCountries = event.value;
    this.checkOtherCountriesChanged();
  }

  selectAllOtherCountries(): void {
    this.selectedOtherCountries = this.countries.map(c => c.id);
    this.checkOtherCountriesChanged();
  }

  deselectAllOtherCountries(): void {
    this.selectedOtherCountries = [];
    this.checkOtherCountriesChanged();
  }

  private checkOtherCountriesChanged(): void {
    const current = [...this.selectedOtherCountries].sort();
    const initial = [...this.initialOtherCountries].sort();
    this.otherCountriesChanged = current.length !== initial.length ||
      current.some((v, i) => v !== initial[i]);
  }

  private loadOtherCountries(): void {
    if (!this.companyId) return;

    this.companyOtherCountriesService.get(this.companyId, parseInt(this.selectedYear)).subscribe({
      next: (data: CompanyOtherCountriesDto) => {
        this.selectedOtherCountries = data.countries.map(c => c.countryId);
        this.initialOtherCountries = [...this.selectedOtherCountries];
        this.otherCountriesChanged = false;
      },
      error: (error: any) => {
        const apiError = error.error as ApiErrorResponse;
        if (apiError?.errorCode === ErrorCodes.NFO_YEAR_NO_RECORD ||
            apiError?.errorCode === ErrorCodes.NFO_ENTITY_NOT_FOUND) {
          this.selectedOtherCountries = [];
          this.initialOtherCountries = [];
          this.otherCountriesChanged = false;
        }
      }
    });
  }

  private saveOtherCountriesAsObservable(companyId: string): Observable<any> {
    const request = {
      year: parseInt(this.selectedYear),
      countries: this.selectedOtherCountries.map(id => ({ countryId: id }))
    };

    return this.companyOtherCountriesService.upsert(companyId, request).pipe(
      tap(() => {
        this.initialOtherCountries = [...this.selectedOtherCountries];
        this.otherCountriesChanged = false;
      })
    );
  }

  // Stock Exchanges

  getStockExchangeName(stockExchangeId: string): string {
    return this.stockExchanges.find(s => s.id === stockExchangeId)?.name || '';
  }

  onStockExchangesSelectionChange(event: any): void {
    this.selectedStockExchanges = event.value;
    this.checkStockExchangesChanged();
  }

  selectAllStockExchanges(): void {
    this.selectedStockExchanges = this.stockExchanges.map(s => s.id);
    this.checkStockExchangesChanged();
  }

  deselectAllStockExchanges(): void {
    this.selectedStockExchanges = [];
    this.checkStockExchangesChanged();
  }

  private checkStockExchangesChanged(): void {
    const current = [...this.selectedStockExchanges].sort();
    const initial = [...this.initialStockExchanges].sort();
    this.stockExchangesChanged = current.length !== initial.length ||
      current.some((v, i) => v !== initial[i]);
  }

  private loadStockExchanges(): void {
    if (!this.companyId) return;

    this.companyStockExchangesService.get(this.companyId, parseInt(this.selectedYear)).subscribe({
      next: (data: CompanyStockExchangesDto) => {
        this.selectedStockExchanges = data.stockExchanges.map(s => s.stockExchangeId);
        this.initialStockExchanges = [...this.selectedStockExchanges];
        this.stockExchangesChanged = false;
      },
      error: (error: any) => {
        const apiError = error.error as ApiErrorResponse;
        if (apiError?.errorCode === ErrorCodes.NFO_YEAR_NO_RECORD ||
            apiError?.errorCode === ErrorCodes.NFO_ENTITY_NOT_FOUND) {
          this.selectedStockExchanges = [];
          this.initialStockExchanges = [];
          this.stockExchangesChanged = false;
        }
      }
    });
  }

  private saveStockExchangesAsObservable(companyId: string): Observable<any> {
    const request = {
      year: parseInt(this.selectedYear),
      stockExchanges: this.selectedStockExchanges.map(id => ({ stockExchangeId: id }))
    };

    return this.companyStockExchangesService.upsert(companyId, request).pipe(
      tap(() => {
        this.initialStockExchanges = [...this.selectedStockExchanges];
        this.stockExchangesChanged = false;
      })
    );
  }

  // Capitalization

  onCapitalizationChange(value: number): void {
    this.capitalization = value;
    this.capitalizationChanged = this.capitalization !== this.initialCapitalization;
  }

  private loadCapitalization(): void {
    if (!this.companyId) return;

    this.companyCapitalizationService.get(this.companyId, parseInt(this.selectedYear)).subscribe({
      next: (data: CompanyCapitalizationDto) => {
        this.capitalization = data.capitalization;
        this.initialCapitalization = data.capitalization;
        this.capitalizationChanged = false;
      },
      error: (error: any) => {
        const apiError = error.error as ApiErrorResponse;
        if (apiError?.errorCode === ErrorCodes.NFO_YEAR_NO_RECORD ||
            apiError?.errorCode === ErrorCodes.NFO_ENTITY_NOT_FOUND) {
          this.capitalization = null;
          this.initialCapitalization = null;
          this.capitalizationChanged = false;
        }
      }
    });
  }

  private saveCapitalizationAsObservable(companyId: string): Observable<any> {
    const request = {
      year: parseInt(this.selectedYear),
      capitalization: this.capitalization ?? 0
    };

    return this.companyCapitalizationService.upsert(companyId, request).pipe(
      tap(() => {
        this.initialCapitalization = this.capitalization;
        this.capitalizationChanged = false;
      })
    );
  }

  // Stock Indexes

  get filteredStockIndices(): Index[] {
    const countryId = this.form.get('countryId')?.value;
    const countryIds = new Set<string>();
    if (countryId) countryIds.add(countryId);
    for (const id of this.selectedOtherCountries) {
      countryIds.add(id);
    }
    if (countryIds.size === 0) return [];
    return this.stockIndices.filter(idx =>
      idx.countries?.some(c => countryIds.has(c.id))
    );
  }

  getStockIndexName(stockIndexId: string): string {
    return this.stockIndices.find(s => s.id === stockIndexId)?.name || '';
  }

  onStockIndexesSelectionChange(event: any): void {
    this.selectedStockIndexes = event.value;
    this.checkStockIndexesChanged();
  }

  selectAllStockIndexes(): void {
    this.selectedStockIndexes = this.filteredStockIndices.map(s => s.id);
    this.checkStockIndexesChanged();
  }

  deselectAllStockIndexes(): void {
    this.selectedStockIndexes = [];
    this.checkStockIndexesChanged();
  }

  private checkStockIndexesChanged(): void {
    const current = [...this.selectedStockIndexes].sort();
    const initial = [...this.initialStockIndexes].sort();
    this.stockIndexesChanged = current.length !== initial.length ||
      current.some((v, i) => v !== initial[i]);
  }

  private loadStockIndexes(): void {
    if (!this.companyId) return;

    this.companyStockIndexesService.get(this.companyId, parseInt(this.selectedYear)).subscribe({
      next: (data: CompanyStockIndexesDto) => {
        this.selectedStockIndexes = data.stockIndexes.map(s => s.stockIndexId);
        this.initialStockIndexes = [...this.selectedStockIndexes];
        this.stockIndexesChanged = false;
      },
      error: (error: any) => {
        const apiError = error.error as ApiErrorResponse;
        if (apiError?.errorCode === ErrorCodes.NFO_YEAR_NO_RECORD ||
            apiError?.errorCode === ErrorCodes.NFO_ENTITY_NOT_FOUND) {
          this.selectedStockIndexes = [];
          this.initialStockIndexes = [];
          this.stockIndexesChanged = false;
        }
      }
    });
  }

  private saveStockIndexesAsObservable(companyId: string): Observable<any> {
    const request = {
      year: parseInt(this.selectedYear),
      stockIndexes: this.selectedStockIndexes.map(id => ({ stockIndexId: id }))
    };

    return this.companyStockIndexesService.upsert(companyId, request).pipe(
      tap(() => {
        this.initialStockIndexes = [...this.selectedStockIndexes];
        this.stockIndexesChanged = false;
      })
    );
  }

  onDateFocus(event: Event): void {
    Helpers.onDateInputFocus(event);
  }

  onDateBlur(event: Event): void {
    Helpers.onDateInputBlur(event);
  }

  onYearDateInput(event: Event, controlName: string): void {
    const value = Helpers.onDateInputFormat(event);
    this.form.get(controlName)?.setValue(value, { emitEvent: false });
    this.form.get(controlName)?.markAsDirty();
  }

  private aggregateCleanup(summaries: CleanupSummaryDto[]): CleanupSummaryDto {
    const merged: Record<string, number> = {};
    let total = 0;
    for (const s of summaries) {
      total += s.totalRowsAffected;
      for (const [table, count] of Object.entries(s.rowsAffectedByTable)) {
        merged[table] = (merged[table] ?? 0) + count;
      }
    }
    return { rowsAffectedByTable: merged, totalRowsAffected: total };
  }

  private surfaceCleanupToast(): void {
    const summary = this.lastCleanupSummary;
    if (!summary || summary.totalRowsAffected === 0) return;
    const tableCount = Object.keys(summary.rowsAffectedByTable).length;
    const rows = summary.totalRowsAffected === 1 ? 'row' : 'rows';
    const tables = tableCount === 1 ? 'table' : 'tables';
    this.toast.info(`Removed ${summary.totalRowsAffected} ${rows} across ${tableCount} ${tables}.`);
  }
}
